import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Calendar, MapPin, Users, Coins, Trophy } from "lucide-react";
import AppLayout from "../components/AppLayout";
import SportIcon from "../components/SportIcon";
import InvitePlayersDialog from "../components/InvitePlayersDialog";
import EventShareDialog from "../components/EventShareDialog";
import AddToCalendarButton from "../components/AddToCalendarButton";
import AttendanceWidget from "../components/AttendanceWidget";
import ResultDialog from "../components/ResultDialog";
import GolfScorecard from "../components/GolfScorecard";
import GolfLeaderboard from "../components/GolfLeaderboard";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { formatDateTime, formatRange } from "../components/MatchCard";

export default function MatchDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(false);

  const [scoreSignal, setScoreSignal] = useState(0);

  const fetchMatch = () => api.get(`/matches/${id}`).then((r) => setMatch(r.data));
  useEffect(() => { fetchMatch(); }, [id]); // eslint-disable-line

  if (!match) return <AppLayout><div className="pp-card p-10 text-center text-zinc-500">Loading...</div></AppLayout>;

  const isGolfMatch = match.sport?.slug === "golf" && !!match.golf;
  const joinedPlayers = match.players?.filter((p) => p.status === "joined") || [];
  const max = match.max_players || 4;
  const isMember = user && match.players?.some((p) => p.user_id === user.id);
  const isCreator = user && match.created_by === user.id;
  const canRecord = isCreator || (user && ["admin", "club_admin", "federation"].includes(user.role));

  const join = async () => {
    if (!user) { navigate("/login"); return; }
    setLoading(true);
    try {
      await api.post(`/matches/${id}/join`);
      toast.success(t("joined"));
      fetchMatch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  const cancel = async () => {
    setLoading(true);
    try {
      await api.post(`/matches/${id}/cancel`);
      toast.success(t("cancelled"));
      fetchMatch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const recordResult = async (winner_id, score_a, score_b) => {
    try {
      await api.post(`/matches/${id}/result`, {
        winner_id,
        score_summary: `${score_a}-${score_b}`,
      });
      toast.success("Result recorded");
      fetchMatch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <AppLayout>
      <Link to="/matches" data-testid="back-to-matches" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1 mb-6"><ArrowLeft size={14} /> {t("matches")}</Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 pp-card-elevated p-8">
          <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider">
            <SportIcon slug={match.sport?.slug || "padel"} size={14} /> {match.sport?.name}
            {(match.visibility === "private" || match.match_type === "private") && (
              <span className="pp-chip text-[10px] text-[#FFD60A] border-[#FFD60A]/40" data-testid="match-private-badge">Private</span>
            )}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black mt-2" data-testid="match-title">{match.title}</h1>
          <p className="text-zinc-400 mt-3">{match.description}</p>

          <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
            <Info icon={<Calendar size={16} />} label={t("date")} value={formatRange(match.start_time, match.end_time, match.all_day)} />
            <Info icon={<Users size={16} />} label={t("max_players")} value={`${joinedPlayers.length}/${max}`} />
            <Info icon={<MapPin size={16} />} label={t("location")} value={match.club?.name || match.location || "—"} />
            <Info icon={<Coins size={16} />} label={t("price_per_player")} value={match.price_per_player > 0 ? `${match.price_per_player} ${match.currency}` : t("free")} />
          </div>

          {match.status === "completed" && (
            <div className="mt-6 pp-card p-4 border-[#00E676]/30">
              <div className="flex items-center gap-2 text-[#00E676] font-bold"><Trophy size={16} /> Result</div>
              <div className="text-sm text-zinc-300 mt-1">Score: {match.score_summary || "—"}</div>
              <div className="text-xs text-zinc-500 mt-1">Winner: {match.players?.find((p) => p.user_id === match.winner_id)?.user?.first_name || "—"}</div>
            </div>
          )}

          <div className="mt-8 flex gap-3 flex-wrap">
            {!isMember && match.status === "open" && (
              <button data-testid="match-join-btn" onClick={join} disabled={loading} className="pp-btn-primary px-6 py-3 rounded-xl">{t("join_match")}</button>
            )}
            {isMember && match.status !== "completed" && <span className="pp-chip pp-chip-active" data-testid="match-joined-chip">{t("joined")}</span>}
            {match.status !== "completed" && (
              <AddToCalendarButton
                kind="match"
                id={id}
                start={match.start_time}
                end={match.end_time}
                title={match.title}
                location={match.location}
                description={match.description}
                testIdSuffix="match"
              />
            )}
            {isCreator && match.status === "open" && (
              <InvitePlayersDialog eventKind="match" eventId={id} onInvited={fetchMatch} />
            )}
            {isCreator && (
              <EventShareDialog eventKind="match" eventId={id} eventTitle={match.title} />
            )}
            {isCreator && match.status === "open" && (
              <button data-testid="match-cancel-btn" onClick={cancel} className="pp-btn-secondary px-6 py-3 rounded-xl">{t("cancel")}</button>
            )}
            {canRecord && match.status === "open" && joinedPlayers.length === 2 && (
              <ResultDialog
                trigger={<button data-testid="match-record-btn" className="pp-btn-secondary px-6 py-3 rounded-xl flex items-center gap-2"><Trophy size={14} /> Record result</button>}
                title="Record match result"
                playerA={{ id: joinedPlayers[0].user_id, name: `${joinedPlayers[0].user?.first_name || ""}` }}
                playerB={{ id: joinedPlayers[1].user_id, name: `${joinedPlayers[1].user?.first_name || ""}` }}
                onSubmit={({ score_a, score_b }) => {
                  const winner = score_a > score_b ? joinedPlayers[0].user_id : joinedPlayers[1].user_id;
                  recordResult(winner, score_a, score_b);
                }}
                testIdPrefix="match-result"
              />
            )}
          </div>

          {isGolfMatch && isMember && (
            <div className="mt-6">
              <GolfScorecard event={match} eventKind="match" onSaved={() => setScoreSignal((s) => s + 1)} />
            </div>
          )}
          {isGolfMatch && (
            <div className="mt-4">
              <GolfLeaderboard eventId={id} eventKind="match" refreshSignal={scoreSignal} />
            </div>
          )}
        </div>

        <aside className="pp-card p-6">
          <h3 className="font-heading text-xl font-bold mb-4">{t("player_col")}s</h3>
          <div className="mb-4">
            <AttendanceWidget eventKind="match" eventId={id} />
          </div>
          <div className="space-y-3" data-testid="match-players">
            {match.players?.map((p) => (
              <div key={p.id} className="flex items-center gap-3">
                <Avatar className="h-9 w-9 border border-white/10">
                  <AvatarImage src={p.user?.avatar_url || ""} />
                  <AvatarFallback className="bg-[#007AFF] text-white">{(p.user?.first_name?.[0] || "?")}</AvatarFallback>
                </Avatar>
                <div className="text-sm">
                  <div className="font-medium">{p.user?.first_name} {p.user?.last_name}</div>
                  <div className="text-xs text-zinc-500">{p.status} {p.user?.elo ? `· ${p.user.elo} ELO` : ""}</div>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </AppLayout>
  );
}

function Info({ icon, label, value }) {
  return (
    <div className="pp-card p-3">
      <div className="text-xs text-zinc-500 uppercase tracking-wider flex items-center gap-2">{icon} {label}</div>
      <div className="font-medium mt-1">{value}</div>
    </div>
  );
}
